/**
 * SSO POST handler utility
 * Handles form POST requests to /auth endpoint similar to auth.aspx
 */

import { cookieUtils } from '@/utils/auth/cookieUtils';
import { log } from '@/utils/core/vueLogger';

export const ssoPostHandler = {
	/**
	 * Initialize POST handler for SSO
	 * Call this in your router or main entry point
	 */
	init() {
		// Listen for form submissions to auth endpoint
		this.setupFormInterceptor();

		// Handle any existing POST data on page load
		this.handlePageLoadSSO();
	},

	/**
	 * Setup form interceptor for SSO forms
	 */
	setupFormInterceptor() {
		document.addEventListener('submit', (event) => {
			event.preventDefault();
			const form = event.target;

			// Check if this is an SSO form targeting auth endpoint
			if (form.action && (form.action.includes('/auth') || form.action.includes('auth.aspx'))) {
				event.preventDefault();
				this.handleFormSubmit(form);
			}
		});
	},

	/**
	 * Handle SSO form submission
	 * @param {HTMLFormElement} form - Form element
	 */
	async handleFormSubmit(form) {
		try {
			const formData = new FormData(form);
			const ssoParams = {};

			// Extract form data
			for (let [key, value] of formData.entries()) {
				ssoParams[key] = value;
			}

			log('SSO POST Handler: Processing form submission', ssoParams);

			// Set cookies (replacing auth.aspx functionality)
			cookieUtils.setSSOCookies(ssoParams);

			// Redirect to Vue SSO handler
			window.location.href = `/auth?${new URLSearchParams(ssoParams).toString()}`;
		} catch (error) {
			console.error('SSO POST Handler: Error processing form', error);
		}
	},

	/**
	 * Handle SSO data on page load (for direct POST to current page)
	 */
	handlePageLoadSSO() {
		// Check if we have SSO parameters in the current URL
		const urlParams = new URLSearchParams(window.location.search);
		const hasSSO = ['u', 'i', 'a', 'action', 'origin', 'd', 'r', 'm', 'rp'].some((key) => urlParams.has(key));

		if (hasSSO) {
			const ssoParams = {};
			['u', 'i', 'a', 'action', 'origin', 'd', 'r', 'm', 'rp', 'sso'].forEach((key) => {
				if (urlParams.has(key)) {
					ssoParams[key] = urlParams.get(key);
				}
			});

			if (Object.keys(ssoParams).length > 0) {
				log('SSO POST Handler: Found SSO parameters on page load', ssoParams);
				cookieUtils.setSSOCookies(ssoParams);

				// Clean URL and redirect to auth handler
				window.history.replaceState({}, '', window.location.pathname);
				setTimeout(() => {
					window.location.href = '/auth';
				}, 100);
			}
		} else if (window.location.search.length > 1) {
			// Legacy SSO: ?DEMOCLIENT format — the entire query string is the customer reference.
			// The iframe loads at /m.eagle/default.htm (no querystring), so the Vue layer must
			// extract the code and set the cookies before the iframe's Startup.js runs.
			const ssoCode = window.location.search.slice(1);

			if (ssoCode === 'reset') {
				['__sso', '__sso_auto', '__u', '__a', '__d', '__rp', '__i', '__c', '__r', '__m', '__p', '__la', '__action', '__origin'].forEach((name) => {
					cookieUtils.deleteCookie(name);
				});
				localStorage.removeItem('ecosSsoCustomerCode');
			} else {
				log('info', 'ssoPostHandler', `Found legacy SSO code on page load: ${ssoCode}`);
				cookieUtils.setEncodedCookie('__sso', ssoCode);
				cookieUtils.setEncodedCookie('__sso_auto', '1');
				localStorage.setItem('ecosSsoCustomerCode', JSON.stringify({ code: ssoCode, ts: Date.now() }));
			}

			// Clean the URL without reloading — the iframe will read the cookies on load
			window.history.replaceState({}, '', window.location.pathname);
		} else {
			// No SSO signal in the URL (e.g. installed PWA relaunch at the static manifest
			// start_url "/"). Restore __sso from localStorage if present and not expired,
			// but never __sso_auto — this brings back the button, not an auto-redirect.
			this.restoreSsoCookieFromLocalStorage();
		}
	},

	/**
	 * Restore the __sso cookie from a persisted legacy SSO code when the URL carries
	 * no SSO signal (installed PWAs always relaunch at the manifest's static start_url,
	 * so the ?<code> query string set at install time is never seen again).
	 */
	restoreSsoCookieFromLocalStorage() {
		if (cookieUtils.getDecodedCookie('__sso')) return;

		const raw = localStorage.getItem('ecosSsoCustomerCode');
		if (!raw) return;

		try {
			const { code, ts } = JSON.parse(raw);
			const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
			if (!code || Date.now() - ts > sevenDaysMs) {
				localStorage.removeItem('ecosSsoCustomerCode');
				return;
			}
			log('info', 'ssoPostHandler', `Restoring persisted legacy SSO code from localStorage: ${code}`);
			cookieUtils.setEncodedCookie('__sso', code);
		} catch (e) {
			localStorage.removeItem('ecosSsoCustomerCode');
		}
	},

	/**
	 * Create SSO redirect URL
	 * @param {Object} ssoParams - SSO parameters
	 * @returns {string} - Redirect URL
	 */
	createRedirectUrl(ssoParams) {
		const baseUrl = window.location.origin;
		const params = new URLSearchParams(ssoParams);
		return `${baseUrl}/auth?${params.toString()}`;
	},
};
